import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { fetchJson } from "@/lib/api";

// Phase 2a rental-agreement — react-query hooks for the customer-side
// signing flow. The order_id is required for every call so the API
// route can scope to the signed-in user's order.

export interface AgreementPayload {
  agreement_id: number;
  template_version: string;
  apparent_agent_required: boolean;
  initialed_page_numbers: number[];
  pages_html: string[];
  pages_required_anchors: Record<string, string[]>;
  completed_at: string | null;
}

export function agreementKey(orderId: number): readonly unknown[] {
  return ["checkout", "agreement", orderId];
}

export function useAgreement(orderId: number): UseQueryResult<AgreementPayload> {
  return useQuery({
    queryKey: agreementKey(orderId),
    queryFn: () => fetchJson<AgreementPayload>(`/api/v1/checkout/agreement?order_id=${orderId}`)
  });
}

export interface PageSubmitBody {
  order_id: number;
  page_number: number;
  initials: string;
}

export function useSubmitPage(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PageSubmitBody) =>
      fetchJson<{ initialed_page_numbers: number[] }>("/api/v1/checkout/agreement/page", {
        method: "POST",
        body: { ...body }
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: agreementKey(orderId) })
  });
}

export interface FinalizeBody {
  order_id: number;
  signature_data_url: string;
  signature_type: "drawn" | "typed";
  printed_name: string;
  signed_date: string;
  apparent_agent_initials?: string;
}

export function useFinalizeAgreement(orderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FinalizeBody) =>
      fetchJson<{ completed_at: string }>("/api/v1/checkout/agreement/finalize", {
        method: "POST",
        body: { ...body }
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: agreementKey(orderId) })
  });
}
